import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import CheckoutCart, { type CheckoutCartLine } from '@/components/CheckoutCart';

/**
 * ── /components/checkout-cart ────────────────────────────────────────────
 *
 * CheckoutCart — the itemised cart box on payment confirmation / detail
 * screens. Render-only (see the component's own docstring): every amount
 * here arrives PRE-FORMATTED, "0 reads Free" already applied. This preview
 * never does money math either — fixtures hardcode the display strings the
 * real callers would hand it:
 *   - app/events/[id]/confirm.tsx — `headerLabel="Cart"`, `lines`/
 *     `totalDisplay` come straight from the cart context
 *     (src/lib/eventCart.tsx's `formatAmount`, computed client-side from the
 *     selected slot/tiers).
 *   - app/bookings/[id]/index.tsx and app/requests/[id]/index.tsx —
 *     `headerLabel="Tickets"`, lines built from `tickets.map(...)` using the
 *     SERVER's own pre-formatted `ticket.lineTotal.display` /
 *     `booking.subtotal.display` — nothing summed client-side there, so a
 *     coupon-discounted `amountDue` still reads correctly.
 * All three real call sites override `headerLabel`; the "Ticket(s)" default
 * is never actually seen live — see the headerLabel block below.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Three lines mirroring a real multi-tier event: General is free, VIP is
// paid × 2, and Chef's Counter is a deliberately long tier name. Total is
// the pre-summed string a caller would hand over — this box never adds.
const PREVIEW_CART_LINES: CheckoutCartLine[] = [
  { id: 'general', name: 'General', quantity: 1, subtotalDisplay: 'Free' },
  { id: 'vip', name: 'VIP', quantity: 2, subtotalDisplay: '₹3,000' },
  {
    id: 'premium',
    name: "Chef's Counter — Reserved Seating with Off-Menu Courses",
    quantity: 1,
    subtotalDisplay: '₹4,500',
  },
];
const PREVIEW_CART_TOTAL = '₹7,500';

// Single line — the smallest real shape (a one-tier event, or a request with
// one ticket type). Confirms the quantity/price columns don't lean on a
// sibling row for their right-alignment.
const PREVIEW_SINGLE_LINE: CheckoutCartLine[] = [
  {
    id: 'general',
    name: 'General Admission',
    quantity: 1,
    subtotalDisplay: '₹1,200',
  },
];

// All-free — an RSVP-style event where every tier is ₹0. Same formatter, same
// "0 reads Free" rule as PREVIEW_CART_LINES' `general` line above; nothing
// special-cased here, the total just happens to land on "Free" too.
const PREVIEW_ALL_FREE_LINES: CheckoutCartLine[] = [
  { id: 'general', name: 'General', quantity: 2, subtotalDisplay: 'Free' },
];

// Zero lines — `lines.map()` over an empty array just renders no rows, so the
// component itself doesn't guard against this. No real screen ever reaches
// it though: confirm.tsx's own render guards on
// `lines.length === 0` and returns null before CheckoutCart ever mounts
// (see app/events/[id]/confirm.tsx). Kept here only as a defensive check that
// an empty cart still renders a sane header/divider/total, not a blank gap.
const PREVIEW_EMPTY_LINES: CheckoutCartLine[] = [];

export default function CheckoutCartPreview() {
  return (
    <>
      <Head>
        <title>CheckoutCart · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Base composition — free + paid + a long tier name, default
            headerLabel. Note the long name TRUNCATES to one line rather than
            wrapping: the component's own docstring says "description on left
            (wraps)", but the row is built with PSText.Body2's `ellipsis`
            prop, which Tamagui maps to `numberOfLines={1}` + CSS
            text-overflow — a wrap is what the comment promises, a clip is
            what the prop actually does. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              free + paid + long-name lines, a muted divider, then the summed
              total — long names truncate to one line (ellipsis), they
              don&apos;t wrap despite the component&apos;s docstring
            </Text>
            <YStack alignSelf="stretch">
              <CheckoutCart
                lines={PREVIEW_CART_LINES}
                totalDisplay={PREVIEW_CART_TOTAL}
              />
            </YStack>
          </YStack>

          {/* headerLabel — every real call site overrides this. confirm.tsx
            calls it "Cart"; bookings/[id] and requests/[id] both call it
            "Tickets". The default below ("Ticket(s)") is what you get if a
            caller omits the prop, but no live screen currently does. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              headerLabel — default &quot;Ticket(s)&quot; (unused by any live
              screen) vs confirm.tsx&apos;s &quot;Cart&quot; vs
              bookings/requests&apos; &quot;Tickets&quot;
            </Text>
            <YStack alignSelf="stretch" gap="$md">
              <CheckoutCart lines={PREVIEW_SINGLE_LINE} totalDisplay="₹1,200" />
              <CheckoutCart
                headerLabel="Cart"
                lines={PREVIEW_SINGLE_LINE}
                totalDisplay="₹1,200"
              />
              <CheckoutCart
                headerLabel="Tickets"
                lines={PREVIEW_SINGLE_LINE}
                totalDisplay="₹1,200"
              />
            </YStack>
          </YStack>

          {/* Single line — the smallest real shape, e.g. a one-tier event or
            a single-ticket-type request. Confirms the box doesn't need a
            second row to lay out its columns correctly. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              single line — one-tier event or a one-ticket-type request
            </Text>
            <YStack alignSelf="stretch">
              <CheckoutCart
                headerLabel="Tickets"
                lines={PREVIEW_SINGLE_LINE}
                totalDisplay="₹1,200"
              />
            </YStack>
          </YStack>

          {/* All-free — an RSVP-style event where every line AND the total
            read "Free". Same formatter as the base composition's `general`
            line above; shown standalone so the all-zero case is legible
            without a paid line beside it for contrast. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              all-free — RSVP-style event, every line and the total read
              &quot;Free&quot;
            </Text>
            <YStack alignSelf="stretch">
              <CheckoutCart
                lines={PREVIEW_ALL_FREE_LINES}
                totalDisplay="Free"
              />
            </YStack>
          </YStack>

          {/* Empty lines — a defensive-only edge case (see the fixture's own
            comment above): the component renders header + empty box +
            divider + total rather than breaking, even though no live screen
            ever hands it zero lines. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              empty lines — defensive only; confirm.tsx guards this away before
              CheckoutCart ever mounts
            </Text>
            <YStack alignSelf="stretch">
              <CheckoutCart lines={PREVIEW_EMPTY_LINES} totalDisplay="₹0" />
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
